
import {XdSentry}                        from "../../libs/sentry/xd-sentry";
import {cs}                              from "../../react/chain-services";
import {consumeContext}                  from "../../react/context";
import {Invoke}                          from "../../react/invoke";
import {Load2}                           from "../../react/load2";
import {scn}                             from "../../react/state-change-nested-value";
import {UseState}                        from "../../react/use-state";
import {chain}                           from "../../utils/fs";
import {arrMapToO, equalDeep, mapValues} from "../../utils/objects";
import {waitTimeout}                     from "../../utils/wait-timeout";
import {loadScript}                      from "../browser/load-script";
import {isDevQuan}                       from "../debug/is-dev-quan";
import {LazyLoad2} from "@Common/react/async/lazy-load2";
import {RetryingLoad2} from "@Common/react/async/retrying-load2";

export const BraintreeIntegration = ({defaultStyle, next: rootNext}) => cs(
    ({}, next) => 0&&isDevQuan() ? (
        rootNext({
            renderForm: ({next}) => next({
                getFieldState: () => ({
                    valid: true,
                    focused: true,
                }),
                isValid: () => true,
                tokenize: async () => ({
                    nonce: "Test-Nonce",
                    details: {

                    },
                }),
                initFields: () => null,
            }),
        })
    ) : (
        next()
    ),

    consumeContext("apis"),
    ["braintreeScript", ({}, next) => Load2({
        captureException: true,
        fetch: async () => {
            await Promise.all([
                loadScript({src: "https://js.braintreegateway.com/web/3.52.0/js/client.js", id: "braintree-client"}),
                loadScript({src: "https://js.braintreegateway.com/web/3.52.0/js/hosted-fields.js", id: "braintree-hosted-fields"}),
            ]);
            return window["braintree"];
        },
        next,
    })],

    ({apis, braintreeScript}) => rootNext({
        renderForm: ({recaptchaToken, next}) => cs(
            ["state", (_, next) => UseState({next})],
            ["authToken", (_, next) => cs(
                ["load2", (_, next) => Load2({
                    fetch: () => apis.next.creditCard.getAuthorizationToken({recaptchaToken}),
                    next,
                })],
                ["retrying", ({load2}, next) => RetryingLoad2({
                    load2,
                    retryWhen: (v) => v == null,
                    next,
                })],
                ({retrying}) => next(retrying),
            )],
            ({state, authToken}) => next({
                getFieldState: (id) => state.value?.fields?.[id],
                isValid: () => !fields.find((f) => !state.value?.fields?.[f.name].valid),
                hostedFieldsInstance: state.value?.hostedFieldsInstance,
                loading: !braintreeScript.exception && !state.value,
                initException: (
                    !authToken.loading && !authToken.value ? (
                        {
                            noToken: true,
                            message: "no token",
                        }
                    ) : state.value?.initException ? (
                        {
                            sdk: state.value.initException,
                            message: "sdk run error",
                        }
                    ) : braintreeScript.exception ? (
                        {
                            scriptLoading: braintreeScript.exception,
                            message: "sdk load error",
                        }
                    ) : (
                        null
                    )
                ),

                tokenize: async () => new Promise((resolve, reject) => {
                    state.value.hostedFieldsInstance.tokenize((err, payload) => {
                        if (err) {
                            reject(err);
                            return;
                        }

                        // This is where you would submit payload.nonce to your server
                        resolve(payload);
                    });
                }),

                initFields: ({getSelector, style=undefined, prefills, hostedFieldsRef}) => (
                    !authToken.loading && !braintreeScript.loading && !braintreeScript.exception && Invoke({
                        action: () => cs(
                            async ({}, next) => {
                                try {
                                    await retryAsync(next, 3);
                                } catch (e) {
                                    XdSentry.captureException(e);
                                    console.log("Braintree init exception", e);
                                    state.onChange({
                                        initException: e,
                                    });
                                }
                            },
                            async () => {
                                const clientInstance = await createBraintreeClient({
                                    authorization: authToken.value,
                                    script: braintreeScript.value,
                                });

                                const hostedFieldsInstance1 = await createBraintreeHostedFields({
                                    client: clientInstance,
                                    script: braintreeScript.value,
                                    styles: {
                                        'input': {
                                            'font-family': 'Arial',
                                            ...defaultStyle?.input,
                                            ...style?.input,
                                        },
                                        '::placeholder': {
                                            // 'color': '#a5aec0'
                                            'color': '#DCDCDC',
                                            'font-weight': 'lighter',
                                        },
                                        ':focus': {
                                            ...defaultStyle?.input,
                                            ...style?.input,
                                        },
                                        '.valid': {
                                            ...defaultStyle?.input,
                                            ...style?.input,
                                        },
                                        '.invalid': {
                                            'color': "#aa2823"
                                        }
                                    },
                                    fields: arrMapToO(
                                        fields,
                                        (f) => ({
                                            selector: getSelector(f.name),
                                            placeholder: f.placeholder,
                                            ...chain(
                                                prefills?.[f.name],
                                                (_) => _ && {
                                                    prefill: _,
                                                },
                                            ),
                                            // prefill
                                        }),
                                        (f) => f.name,
                                    ),
                                });

                                const captureState = (e) => {
                                    const newState = toState(e.fields);
                                    if (!equalDeep(newState, state.value?.fields)) {
                                        scn(state, ["fields"], () => newState);
                                    }
                                };
                                hostedFieldsInstance1.on("focus", captureState);
                                hostedFieldsInstance1.on("blur", captureState);
                                hostedFieldsInstance1.on("empty", captureState);
                                hostedFieldsInstance1.on("notEmpty", captureState);
                                hostedFieldsInstance1.on("validityChange", captureState);

                                hostedFieldsRef?.(hostedFieldsInstance1);

                                state.onChange({
                                    fields: (() => {
                                        const initFieldState = {
                                            empty: true,
                                            focused: false,
                                            potentiallyValid: false,
                                            valid: false,
                                        };
                                        return arrMapToO(fields, () => initFieldState, (f) => f.name);
                                    })(),
                                    hostedFieldsInstance: hostedFieldsInstance1,
                                });
                            },
                        ),
                    })
                ),
            }),
        )
    }),
);

const toState = (eventFields) => {
    return mapValues(eventFields, (f) => ({
        empty: f.isEmpty,
        focused: f.isFocused,
        potentiallyValid: f.isPotentiallyValid,
        valid: f.isValid,
    }));
};

const createBraintreeClient = async ({authorization, script: braintree}) => new Promise((resolve, reject) => {
    braintree.client.create({authorization}, function (err, clientInstance) {
        if (err) {
            reject(err);
        } else {
            resolve(clientInstance);
        }
    });
});

const createBraintreeHostedFields = async ({script: braintree, ...config}) => new Promise<any>((resolve, reject) => {
    braintree.hostedFields.create(config, function (err, hostedFieldsInstance) {
        if (err) {
            reject(err);
        } else {
            resolve(hostedFieldsInstance);
        }
    });
});


const fields = [
    {
        name: "number",
        placeholder: '1111 1111 1111 1111'
    },
    {
        name: "cvv",
        placeholder: '111'
    },
    {
        name: "expirationDate",
        placeholder: 'MM/YY'
    },
    // {
    //     name: "postalCode",
    //     placeholder: '11111'
    // },
];

const retryAsync = async (fn, times) => {
    for (let i = 0; i < times - 1; i++) {
        try {
            return await fn();
        } catch (e) {
            await waitTimeout(3000);
            // Ignore exception
        }
    }

    // This time if exception occurs, it will fail for real
    return await fn();
};
